import type { ValorantDuoStats, ValorantServer } from 'generated/openapi';
import { SignalHigh } from 'lucide-react';
import { memo, type ReactNode, useMemo } from 'react';

import Bullseye from '@/assets/icons/bullseye.svg?react';
import CrossedSwords from '@/assets/icons/crossed-swords.svg?react';
import Medal from '@/assets/icons/medal.svg?react';
import SkullAndCrossbones from '@/assets/icons/skull-and-crossbones.svg?react';
import Trophy from '@/assets/icons/trophy.svg?react';
import TwoOClock from '@/assets/icons/two-oclock.svg?react';
import { SERVER_DISPLAY_NAMES } from '@/features/games/valorant/utils/servers';
import { Card } from '@/shared/components/ui/card';
import { formatDuration } from '@/shared/utils/duration';

const DuoActivityStatItem = ({
  icon,
  value,
  label,
}: {
  icon: ReactNode;
  value: string;
  label: string;
}) => (
  <div className="flex gap-1">
    <div className="text-cold-300 mb-0.5">{icon}</div>
    <div className="flex flex-col gap-1.5">
      <p className="text-cold-50 text-lg leading-5 font-black">{value}</p>
      <p className="text-cold-300 text-xs leading-none font-medium">{label}</p>
    </div>
  </div>
);

interface DuoActivityStatsProps {
  stats: ValorantDuoStats;
  mutualServers?: ValorantServer[];
}

export const DuoActivityStats = memo(function DuoActivityStats({
  stats,
  mutualServers,
}: DuoActivityStatsProps) {
  const kdRatio = useMemo(() => {
    if (stats.deaths === 0) return '0.0';
    return (stats.kills / stats.deaths).toFixed(1);
  }, [stats.kills, stats.deaths]);

  const gameWinRatePercent = useMemo(() => {
    return Math.round(stats.winRate * 100);
  }, [stats.winRate]);

  const roundWinRatePercent = useMemo(() => {
    return Math.round(stats.roundWinRate * 100);
  }, [stats.roundWinRate]);

  return (
    <Card variant="data">
      <p className="text-cold-300 text-sm leading-5 font-bold">
        Mutual Duo stats
      </p>
      <div className="grid grid-cols-2 items-center gap-x-2 gap-y-3">
        <DuoActivityStatItem
          icon={<TwoOClock className="size-5" />}
          value={formatDuration(stats.timePlayed)}
          label="Time spent"
        />
        <DuoActivityStatItem
          icon={<CrossedSwords className="size-5" />}
          value={stats.matchCount.toString()}
          label="Total matches"
        />
        <DuoActivityStatItem
          icon={<Trophy className="size-5" />}
          value={`${gameWinRatePercent.toString()}%`}
          label="Game win%"
        />
        <DuoActivityStatItem
          icon={<Medal className="size-5" />}
          value={`${roundWinRatePercent.toString()}%`}
          label="Round win%"
        />
        <DuoActivityStatItem
          icon={<Bullseye className="size-5" />}
          value={Math.round(stats.damagePerRound).toString()}
          label="Dmg/round"
        />
        <DuoActivityStatItem
          icon={<SkullAndCrossbones className="size-5" />}
          value={kdRatio}
          label="K/D ratio"
        />
      </div>
      {mutualServers && mutualServers.length > 0 && (
        <div className="flex items-center gap-2 pt-1">
          <SignalHigh className="text-cold-300 size-5" />
          <span className="text-cold-300 text-xs leading-none font-medium">
            Mutual servers:{' '}
            {mutualServers.map(s => SERVER_DISPLAY_NAMES[s]).join(', ')}
          </span>
        </div>
      )}
    </Card>
  );
});
